import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AYAR_BOLUMLERI, BOLUM_BASLIGI, type AyarBolumu } from "@koclukpanel/contracts";
import { Alan, BosDurum, Buton, Iskelet, Kart, Rozet } from "@koclukpanel/ui";
import { ApiHatasi, guvenlikAyarlari, guvenlikAyariYaz, type GuvenlikAyari } from "./api";
import { useSudoKapisi } from "./SudoKapisi";
import { GirisDenemeleri, KilitliHesaplar } from "./GuvenlikKilit";
import { AcilDurum } from "./GuvenlikAcil";
import { GuvenlikGrafikleri } from "./GuvenlikGrafik";
import { AcikOturumlar } from "./GuvenlikOturum";
import { GuvenlikPano } from "./GuvenlikPano";

/**
 * GÜVENLİK KONSOLU — AYARLAR — F-SEC-02/03/04/07/09
 *
 * ================================================================
 * EKRAN KATALOGDAN ÇİZİLİYOR
 * ================================================================
 * Alan listesi burada YAZILI DEĞİL; sunucudan geliyor ve sunucu da
 * `packages/contracts/src/guvenlik-ayari.ts` kataloğundan üretiyor.
 * Yani "ayar eklendi ama ekranda yok" hâli yapısal olarak oluşamıyor —
 * bu depoda 05.09.2026'da yedi kez bulunan *"mekanizma var, besleyen
 * yok"* sınıfı bu modülde imkânsız.
 *
 * Ekranın kendi listesi olsaydı, katalog büyüdüğünde sessizce eskirdi
 * ve eksik olan şey **bir güvenlik ayarı** olurdu.
 *
 * ================================================================
 * SINIR YAZMIYORUZ — TAVAN YOK (Emre'nin kararı)
 * ================================================================
 * Emre'ye taban/tavan önerildi ve reddetti: *"Sınır olmasın, tam
 * serbestlik."* Ekran bu yüzden `min`/`max` koymuyor ve girilen değeri
 * kırpmıyor. Reddedilen tek şey ÇALIŞAMAZ değer ve o kararı sunucu
 * veriyor; hata metni de sunucudan geliyor.
 *
 * Ekran kendi kuralını yazsaydı iki yönde de bozulurdu: sunucunun
 * kabul ettiği bir değeri "geçersiz" göstermek, ya da reddedeceği bir
 * değeri "geçerli" gösterip kullanıcıyı boşuna uğraştırmak.
 *
 * ================================================================
 * OKUMA SUDO İSTEMİYOR, DEĞİŞTİRME İSTİYOR — F-SEC-09
 * ================================================================
 * Bu ekran açılırken şifre sormuyor. Sorsaydı F-SEC-01'in *"tek
 * bakışta"* koşulu bozulurdu ve daha kötüsü: rutinleşen bir şifre
 * istemi kullanıcıyı refleksle şifre yazmaya alıştırır, sudo'yu
 * gerçekten gereken yerlerde değersizleştirir.
 *
 * Kaydet'e basıldığında sunucu `SUDO_GEREKLI` dönerse `useSudoKapisi`
 * soruyor ve doğrulandıktan sonra işlem KALDIĞI YERDEN devam ediyor.
 */

/** Kaç ayar varsayılandan sapmış — üstteki özet bunu sayıyor. */
export function sapanSayisi(ayarlar: readonly GuvenlikAyari[]): number {
  return ayarlar.filter((a) => !a.varsayilanMi).length;
}

/**
 * Ayarları bölümlere ayırır.
 *
 * Bölüm sırası KATALOGDAN (`AYAR_BOLUMLERI`) geliyor, sunucunun
 * döndürdüğü sıradan değil: liste sırası bir gün değişirse ekranın
 * bölüm sırası da sessizce değişirdi.
 *
 * Tanınmayan bir bölüm gelirse DÜŞÜRÜLMÜYOR — sonuca ekleniyor.
 * Sunucuya yeni bir bölüm eklenip burası güncellenmezse, o bölümün
 * ayarları ekranda kaybolurdu ve kaybolan şey bir güvenlik ayarı olurdu.
 */
export function bolumlereAyir(
  ayarlar: readonly GuvenlikAyari[],
): Array<{ bolum: string; baslik: string; ayarlar: GuvenlikAyari[] }> {
  const bilinen = new Set<string>(AYAR_BOLUMLERI);
  const sira = [
    ...AYAR_BOLUMLERI,
    ...[...new Set(ayarlar.map((a) => a.bolum))].filter((b) => !bilinen.has(b)),
  ];

  return sira
    .map((bolum) => ({
      bolum,
      baslik: bilinen.has(bolum)
        ? BOLUM_BASLIGI[bolum as AyarBolumu]
        : /*
             Başlığı bilmiyoruz ama ayarı gizlemiyoruz. Kodun kendisi,
             hiçbir şey göstermemekten iyidir.
           */
          bolum,
      ayarlar: ayarlar.filter((a) => a.bolum === bolum),
    }))
    .filter((g) => g.ayarlar.length > 0);
}

function AyarSatiri({ ayar }: { ayar: GuvenlikAyari }) {
  const qc = useQueryClient();
  const [taslak, setTaslak] = useState<string | null>(null);
  const { hataYakala, kapi } = useSudoKapisi(
    "Güvenlik ayarını değiştirmek için şifrenizi doğrulayın.",
  );

  const kaydet = useMutation({
    mutationFn: (deger: number) => guvenlikAyariYaz(ayar.kod, deger),
    onError: hataYakala(() => {
      if (taslak !== null) kaydet.mutate(Number(taslak));
    }),
    onSuccess: async () => {
      setTaslak(null);
      await qc.invalidateQueries({ queryKey: ["guvenlik-ayarlar"] });
    },
  });

  const duzenleniyor = taslak !== null;

  return (
    <div className="border-notr-200 flex flex-col gap-2 border-b py-3 last:border-b-0">
      {kapi}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-notr-900 text-sm font-medium">{ayar.ad}</p>
          <p className="text-notr-600 mt-0.5 text-xs">{ayar.aciklama}</p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {!ayar.varsayilanMi && (
            /*
              SAPMA GÖRÜNÜR OLMAK ZORUNDA. Gevşetilmiş bir ayarın öteki
              on beşin arasında kaybolmaması, F-SEC-05'in tespit
              tarafının en ucuz parçası — ve bugün tek uygulanmış olanı.
            */
            <Rozet ton="uyari">varsayılan değil</Rozet>
          )}
          <span className="text-notr-900 text-sm tabular-nums">
            {ayar.deger}
            {ayar.birim && <span className="text-notr-600 ml-1 text-xs">{ayar.birim}</span>}
          </span>
          {!duzenleniyor && (
            <Buton gorunum="ikincil" onClick={() => setTaslak(String(ayar.deger))}>
              Değiştir
            </Buton>
          )}
        </div>
      </div>

      {duzenleniyor && (
        <div className="bg-notr-50 rounded-alan flex flex-wrap items-end gap-2 p-3">
          <div className="w-40">
            <Alan
              /*
                `Alan` kendi `id`sini `useId` ile üretiyor ve `id`yi
                bilerek dışarı kapatmış: etiket-alan bağını çağıran
                tarafa bırakmak, iki alanın aynı `id`yi taşıdığı gün
                ekran okuyucuyu yanlış alana götürürdü.
              */
              etiket={ayar.birim ? `Yeni değer (${ayar.birim})` : "Yeni değer"}
              /*
                `type="text"` — `number` DEĞİL. Tarayıcının sayı alanı
                geçersiz girdide BOŞ dize döndürüyor, yani "abc" yazan
                kullanıcı sunucudan gelecek açıklayıcı hatayı hiç
                göremiyor, alan sessizce boşalıyordu. Doğrulama kararı
                sunucuda; ekranın işi girdiyi olduğu gibi iletmek.
              */
              type="text"
              inputMode="numeric"
              value={taslak}
              onChange={(e) => setTaslak(e.target.value)}
            />
          </div>
          <Buton
            onClick={() => kaydet.mutate(Number(taslak))}
            yukleniyor={kaydet.isPending}
            yukleniyorMetni="Kaydediliyor"
            /*
              Değişmemiş değeri göndermek, denetime "ayar değişti"
              kaydı düşürürdü — olmayan bir olayı kaydetmek, kaydı
              gürültüye boğar.
            */
            disabled={taslak.trim() === "" || Number(taslak) === ayar.deger}
          >
            Kaydet
          </Buton>
          <Buton gorunum="ikincil" onClick={() => setTaslak(null)}>
            Vazgeç
          </Buton>
          {!ayar.varsayilanMi && (
            <Buton gorunum="ikincil" onClick={() => setTaslak(String(ayar.varsayilan))}>
              Varsayılana dön ({ayar.varsayilan})
            </Buton>
          )}
        </div>
      )}

      {kaydet.isError && (
        /*
          HATA METNİ SUNUCUDAN. Ekran kendi metnini yazsaydı, sunucunun
          çapraz kuralları (taban kilit > azami kilit gibi) burada
          anlatılamazdı — ve anlatılamayan bir ret, kullanıcıya
          "sistem bozuk" gibi görünür.
        */
        <p role="alert" className="text-hata-700 text-sm">
          {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Ayar kaydedilemedi."}
        </p>
      )}
    </div>
  );
}

export function Guvenlik({ onGit }: { onGit: (ekran: string) => void }) {
  const liste = useQuery({
    queryKey: ["guvenlik-ayarlar"],
    queryFn: guvenlikAyarlari,
    retry: false,
  });

  if (liste.isPending) return <Iskelet satir={6} />;

  if (liste.isError) {
    return (
      <Kart baslik="Güvenlik konsolu">
        <BosDurum
          ton="hata"
          baslik="Güvenlik ayarları alınamadı"
          aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      </Kart>
    );
  }

  const ayarlar = liste.data.ayarlar;
  const sapan = sapanSayisi(ayarlar);
  const gruplar = bolumlereAyir(ayarlar);

  return (
    <div className="flex flex-col gap-4">
      {/*
        PANO EN ÜSTTE. Bu ekrana bakan kişi "her şey yolunda mı" diye
        bakıyor; ayarlar ise ancak bir şey yolunda değilse gerekiyor.
        Sırayı tersine koymak, cevabı arkaya gömmek olurdu.
      */}
      <GuvenlikPano onGit={onGit} />

      <Kart
        baslik="Güvenlik konsolu"
        aciklama="Sistemin savunma ayarları. Değişiklikler anında yürürlüğe girer; sunucuyu yeniden başlatmak gerekmez."
      >
        <div className="flex flex-col gap-3">
          <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-notr-600">Toplam ayar</dt>
              <dd className="text-notr-900 tabular-nums">{ayarlar.length}</dd>
            </div>
            <div>
              <dt className="text-notr-600">Varsayılandan sapan</dt>
              <dd className="tabular-nums">
                {/*
                  Uyarı paletinde 700 basamağı YOK; tanımlı olanlar 50,
                  200, 500, 600 ve 800. İlk yazımda olmayan basamağı
                  kullanmıştım: Tailwind tanımsız sınıfı SESSİZCE atıyor,
                  yani derleme geçiyor ve sayı renksiz çıkıyordu.
                  `renk-belirteci` testi yakaladı — tam da bunun için var.

                  Not: o sınıfın tam adı buraya YAZILAMAZ. Tarama kaynağı
                  okuyor ve yorumla kodu ayırmıyor — ki doğrusu bu:
                  ayırsaydı, yorum içine gizlenmiş bir sınıf adı taramadan
                  kaçardı.
                */}
                {sapan === 0 ? (
                  <span className="text-notr-900">yok</span>
                ) : (
                  <span className="text-uyari-800 font-medium">{sapan}</span>
                )}
              </dd>
            </div>
          </dl>

          {/*
            SAYI DEĞİL, CÜMLE. "3 ayar sapmış" bir gösterge değil bir
            uyarıdır ve neyin riskli olduğunu söylemezse süs olur.
            Bu depoda "ekranda var ama hiçbir şeye bağlanmıyor" hatası
            daha önce 25 alanda ölçüldü (C38).
          */}
          {sapan > 0 && (
            <p className="text-notr-700 text-sm">
              Varsayılandan farklı {sapan} ayar var. Sapma tek başına bir sorun değildir — ama
              gevşetilmiş bir ayarın görünmez kalması sorundur. Aşağıda{" "}
              <span className="font-medium">varsayılan değil</span> etiketiyle işaretli.
            </p>
          )}

          <p className="text-notr-600 text-xs">
            Bu ayarlarda üst sınır yoktur: büyük değerler kabul edilir. Reddedilen tek şey çalışamaz
            değerdir — sıfır ya da negatif süre, sayı olmayan alan ve birbiriyle çelişen iki ayar.
            Her değişiklik şifre doğrulaması ister ve denetim kaydına geçer.
          </p>
        </div>
      </Kart>

      {/*
        KILITLI HESAPLAR AYARLARDAN ONCE. Bu ekrana bakan kisi cogunlukla
        bir SORUN oldugu icin bakiyor; "kim giremiyor" sorusu, "kilit
        esigi kac" sorusundan once gelir. Ayarlar referans, bu liste
        eylem.

        Bolum KENDI istegini atiyor ve KENDI iznini soruyor
        (`sistem.guvenlik.kilit_ac`). Yetkisi olmayan icin yalnizca bu
        kart hata gosterir, ayarlar calismaya devam eder.
      */}
      <KilitliHesaplar />

      {gruplar.map((g) => (
        <Kart key={g.bolum} baslik={g.baslik}>
          <div className="flex flex-col">
            {g.ayarlar.map((a) => (
              <AyarSatiri key={a.kod} ayar={a} />
            ))}
          </div>
        </Kart>
      ))}

      {/*
        ACIK OTURUMLAR AYARLARDAN SONRA: "kim bagli" sorusu, ayarlari
        degistirmeye gelen kisinin onunu kesmemeli ama denemelerden
        once gelmeli — biri KIM oldugunu, oteki KIM OLMAYA CALISTIGINI
        anlatiyor.
      */}
      <AcikOturumlar />

      {/*
        GRAFIKLER OTURUMLARDAN SONRA, ACIL DURUMDAN ONCE. Sira bir
        okuma sirasi: once "su an ne oluyor" (pano), sonra "kim bagli"
        (oturumlar), sonra "zaman icinde ne oldu" (grafikler), en son
        "ne yapabilirim" (acil durum).
      */}
      <GuvenlikGrafikleri />

      {/*
        ACIL DURUM EN SONDA VE BILEREK. Bu kart, ekranda yukari dogru
        okunan her seyin CEVAPSIZ kaldigi durumda basvurulacak yer.
        Basa koysaydik, konsolu her acan kisi once "herkesi cikisa
        dusur" dugmesini gorurdu — bir arac kutusunun en ustune balyoz
        koymak gibi.
      */}
      <AcilDurum />

      {/*
        DENEMELER EN SONDA: ayarlari degistirmeye gelen kisinin onunu
        kesmemeli, ama ayni ekranda olmali — "kilit esigini dusureyim mi"
        sorusunun cevabi tam olarak bu listede.
      */}
      <GirisDenemeleri />
    </div>
  );
}
